<template>
    <div class="cp-calculator">
        <div class="cp-calculator-saving">
            <div class="cp-calculator-saving-inset">
                <div class="cp-calculator-saving-inset-heading">{{ labels['potential-production'] }}</div>
                <div class="cp-calculator-saving-inset-value">
                    <strong>{{ computedProductionKwh }}</strong> {{ labels['potential-production-unit'] }}
                </div>
            </div>

            <div class="cp-calculator-saving-inset">
                <div class="cp-calculator-saving-inset-heading">{{ labels['saving-per-month'] }}</div>
                <div class="cp-calculator-saving-inset-value">
                    <strong>{{ computedSavingMonthly }}</strong> €
                </div>
            </div>

            <div class="cp-calculator-saving-inset">
                <div class="cp-calculator-saving-inset-heading">{{ labels['saving-over-ten-years'] }}</div>
                <div class="cp-calculator-saving-inset-value">
                    <strong>{{ computedSavingTenYears }}</strong> €
                </div>
            </div>
        </div>

        <div class="cp-calculator-heading">{{ labels['heading'] }}</div>

        <div class="cp-calculator-input">
            <div class="cp-calculator-input-legends">
                <p>{{ labels['small-field'] }}</p>
                <p>{{ labels['big-field'] }}</p>
            </div>

            <div class="cp-calculator-input-slider">
                <vue-slider v-model="currentValue" :min="min" :max="max" :interval="step" :tooltip-formatter="tooltipFormat" v-bind="sliderOptions" :lazy="true"></vue-slider>
            </div>

            <div class="cp-calculator-input-legends">
                <p>{{ numberFormat( min, 0, ',', ' ') }} {{ unit.split('|')[0] }}</p>
                <p>&gt; {{ numberFormat( max, 0, ',', ' ') }} {{ unit.split('|')[1] }}</p>
            </div>
        </div>

        <div class="cp-calculator-email" v-if="!simulationSent">
            <input name="email" type="email" :placeholder="labels['placeholder-email']" v-model="email" :class="{error: this.emailError}">

            <button class="btn btn--primary" @click.prevent="handleSubmitResults">{{ labels['receive-these-results'] }}</button>
        </div>

        <div class="cp-calculator-thank-you" v-else>
            <p class="cp-calculator-thank-you-paragraph">{{ labels['thank-you'] }}</p>

            <div class="cp-calculator-thank-you-buttons">
                <a class="btn btn--primary" :href="labels['contact-url']">{{ labels['contact-us-for-estimate'] }}</a>

                <button class="btn btn--link" @click.prevent="handleResetEmail">{{ labels['try-new-simulation'] }}</button>
            </div>
        </div>

        <div class="cp-calculator-footer">
            <p class="cp-calculator-footer-geolocation">
                {{ labels['results-depend-on-geolocation'] }}<br/>
                <span v-if="geolocation.loaded">{{ labels['we-locate-you-here'] }} {{ geolocation.emoji }} {{ geolocation.city }}, {{ geolocation.country }}</span>
            </p>

            <p class="cp-calculator-footer-contact-us" v-html="labels['results-may-not-correspond-to-reality']">
            </p>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import VueSlider from 'vue-slider-component'
import '../../styles/components/vue-slider.scss';

function validate_email(email)
{
    const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return regex.test( email.toLowerCase() );
}

function number_format(number, decimals = 2, dec_point = '.', thousands_point = ' ')
{
    if ( number == null || !isFinite( number ) ) {
        return 0;
    }

    if ( !dec_point ) {
        dec_point = '.';
    }

    if ( !thousands_point ) {
        thousands_point = ',';
    }

    number = parseFloat( number ).toFixed( decimals );

    number = number.replace(".", dec_point );

    const splitNum = number.split( dec_point );

    splitNum[0] = splitNum[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands_point );

    number = splitNum.join( dec_point );

    return number;
}

export default {
    name: "CpCalculator",
    components: {
        VueSlider,
    },
    props: {
        min: {
            type: Number,
            required: true,
        },

        max: {
            type: Number,
            required: true,
        },

        step: {
            type: Number,
            required: true,
        },

        peakPower: {
            type: Number,
            required: true,
        },

        unit: {
            type: String,
            required: true,
        },

        actualPrice: {
            type: Number,
            required: true,
        },

        id: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            labels: {},
            initiated: false,
            computing: false,
            simulationSent: false,
            productionKwh: 0,
            savingMonthly: 0,
            savingTenYears: 0,
            currentValue: this.min,
            tooltipFormat: v => ( v <= 1 ) ? ( number_format( v, 0, ',', ' ') + ' ' + ( this.unit.split('|')[0] ) ) : ( number_format( v, 0, ',', ' ') + ' ' + ( this.unit.split('|')[1] ) ),
            sliderOptions: {
                dotSize: 20,
            },
            email:'',
            emailError: false,
            geolocation: {
                city: '',
                emoji: '',
                country: '',
                latitude: '',
                longitude: '',
                loaded: false,
            },
        };
    },
    watch: {
        email: function ( val ) {
            if ( val !== '' ) {
                this.emailError = false;
            }
        },

        currentValue: function () {
            this.computing = true;

            this.fetchSimulation();
        }
    },
    computed: {
        computedProductionKwh() {
            if ( !this.initiated ) {
                return '------'
            }

            if ( this.computing ) {
                return '+-+-+-'
            }

            return number_format( this.productionKwh, 0, ',', ' ' );
        },

        computedSavingMonthly() {
            if ( !this.initiated ) {
                return '------'
            }

            if ( this.computing ) {
                return '+-+-+-'
            }

            return number_format( this.savingMonthly, 0, ',', ' ' );
        },

        computedSavingTenYears() {
            if ( !this.initiated ) {
                return '------'
            }

            if ( this.computing ) {
                return '+-+-+-'
            }

            return number_format( this.savingTenYears, 0, ',', ' ' );
        }
    },
    methods: {
        numberFormat(number, decimals = 2, dec_point = '.', thousands_point = ' ')
        {
            return number_format( number, decimals, dec_point, thousands_point )
        },

        /**
         * Fetch the geolocation of the user
         */
        handleGeolocation()
        {
            const params = new FormData();
            params.append( 'action', 'geolocation' );

            axios.post( ajaxurl, params ).then( response => {
                this.geolocation = response.data;
            } ).then( () => {
                this.fetchSimulation();
            } ) ;
        },

        /**
         * Show the email form again
         */
        handleResetEmail()
        {
            this.simulationSent = false;
        },

        /**
         * Submit the simulation results by email
         */
        handleSubmitResults()
        {
            if ( this.email === '' || !validate_email( this.email ) ) {
                this.emailError = true;
                return;
            }

            this.emailError = false;
            this.simulationSent = true;

            this.fetchResults( this.email );
        },

        /**
         * Fetch the simulation data
         */
        fetchSimulation()
        {
            this.initiated = true;

            this.fetchResults().then( response => {
                const divider = this.currentValue >= 1000 ? 1000 : 1;
                const multiplier = this.currentValue >= 1000 ? 1 : 1000;

                this.productionKwh = Math.round( response.data.average_annual_production * multiplier );
                this.savingMonthly = Math.round( ( response.data.average_monthly_production / divider ) * this.actualPrice );
                this.savingTenYears = Math.round( ( ( response.data.average_annual_production / divider ) * this.actualPrice ) * 10 );

                this.computing = false;
            } );
        },

        /**
         * Main handler; fetch the simulation results and send an email if passed by arg
         *
         * @param email
         * @returns {Promise<AxiosResponse<any>>}
         */
        fetchResults(email = null)
        {
            const params = new FormData();
            params.append( 'action', 'simulation' );
            params.append( 'lat', this.geolocation.latitude );
            params.append( 'lon', this.geolocation.longitude );
            params.append( 'peak_power', this.currentValue * this.peakPower );
            params.append( 'actual_price', this.actualPrice );

            if ( email !== null ) {
                params.append( 'email', email );
            }

            return axios.post( ajaxurl, params );
        },
    },

    created() {
        this.labels = window.__app.calculator.labels[ this.id ];

        this.handleGeolocation();
    },
}
</script>

<style lang="scss" scoped>
@mixin input-placeholder {
    &.placeholder { @content; }
    &:-moz-placeholder { @content; }
    &::-moz-placeholder { @content; }
    &:-ms-input-placeholder { @content; }
    &::-webkit-input-placeholder { @content; }
}

$primary: #252EB1;
$secondary: #E08DAC;
$success: #61D1C7;
$error: #ef5350;

.cp-calculator {
    font-family: 'Apercu', sans-serif;
    margin-left: auto;
    margin-right: auto;
    border: 1px solid #E8DCAA;
    border-radius: 10px;
    padding: 1rem;
    color: $primary;
    width: 100%;

    @media screen and (min-width: 576px) {
        padding: 2rem;
    }

    @media screen and (min-width: 768px) {
        padding: 4rem;
    }

    @media screen and (min-width: 1200px) {
        width: 1000px;
    }

    .btn {
        font-family: 'Apercu-Bold', sans-serif;
        font-weight: bold;
        font-size: 16px;
        line-height: 20px;
        background: transparent;
        padding: 13px 30px;
        display: block;
        cursor: pointer;
        border: 0;

        &--primary {
            color: $primary;
            border: 2px solid $primary;
            border-radius: 9999px;
            transition: background .3s ease-in-out, color .3s ease-in;

            &:hover {
                background: $primary;
                color: white;
            }
        }

        &--link {
            color: $secondary;
            padding-left: 0;
            padding-right: 0;
            border-radius: 0;
            text-align: left;
            border-bottom: 2px solid transparent;
            transition: border-bottom .15s ease-in;

            &:hover {
                border-bottom: 2px solid $secondary;
            }

            @media screen and (min-width: 768px) {
                padding: 0;
            }
        }

        &:active, &:focus {
            outline: none;
            box-shadow: none;
        }
    }

    a {
        text-decoration: none;
        color: $secondary;
        border-bottom: 1px solid transparent;
        transition: border-bottom .15s ease-in;
        padding-bottom: 0.25rem;

        &:hover {
            border-bottom: 1px solid $secondary;
        }
    }

    &-saving {
        display: flex;
        flex-flow: column;

        @media screen and (min-width: 992px) {
            flex-flow: row nowrap;
        }

        &-inset {
            width: 100%;

            &:not(:last-of-type) {
                margin-bottom: 2rem;
            }

            @media screen and (min-width: 992px) {
                width: calc(100% / 3);
                margin-bottom: 0;
            }

            &-heading {
                font-size: 16px;
                line-height: 28px;
                color: $secondary;
                text-transform: uppercase;
                margin-bottom: 12px;
            }

            &-value {
                font-size: 18px;
                line-height: 28px;

                strong {
                    font-family: 'Apercu-Bold', sans-serif;
                    font-weight: bold;
                    font-size: 30px;
                    line-height: 36px;
                }
            }
        }
    }

    &-heading {
        display: flex;
        flex-flow: row nowrap;
        font-style: normal;
        font-family: 'Apercu-Bold', sans-serif;
        font-weight: bold;
        font-size: 30px;
        line-height: 36px;
        margin: 40px 0;
    }

    &-input {
        margin-bottom: 40px;

        &-legends {
            display: flex;
            flex-flow: row nowrap;
            justify-content: space-between;
            line-height: 24px;
            height: 24px;
        }

        &-slider {
            margin: 14px 0;
        }
    }

    &-email {
        display: flex;
        margin-bottom: 40px;
        flex-flow: column;

        @media screen and (min-width: 768px) {
            flex-flow: row nowrap;
        }

        input {
            background: transparent;
            color: $primary;
            font-size: 16px;
            font-family: 'Apercu-Bold', sans-serif;
            font-weight: bold;
            border: 2px solid $primary;
            border-radius: 10px;
            padding: 13px 24px;
            display: block;
            margin-right: 10px;
            transition: border .3s ease-in;
            width: 100%;
            margin-bottom: 1rem;

            @media screen and (min-width: 768px) {
                width: calc( 50% - 10px);
                margin-bottom: 0;
            }

            &:active, &:focus {
                outline: none;
                box-shadow: none;
            }

            @include input-placeholder {
                color: rgba($primary, .6);
                transition: color .3s ease-in;
            }

            &:-webkit-autofill,
            &:-webkit-autofill:hover,
            &:-webkit-autofill:focus,
            &:-webkit-autofill:active  {
                color: $primary;
                font-size: 16px;
                font-family: 'Apercu-Bold', sans-serif;
                font-weight: bold;
                -webkit-box-shadow: 0 0 0 1000px #FFFFFF inset;
                -webkit-text-fill-color: $primary;
            }

            &.error {
                border: 2px solid $error;

                @include input-placeholder {
                    color: rgba($error, .6);
                }
            }
        }
    }

    &-thank-you {
        margin-bottom: 40px;

        &-paragraph {
            font-weight: bold;
            font-size: 16px;
            line-height: 24px;
            color: $success;
        }

        &-buttons {
            display: flex;
            flex-flow: column;

            @media screen and (min-width: 768px) {
                flex-flow: row nowrap;
            }

            a {
                display: inline-block;
                margin-right: 10px;
                margin-bottom: 1rem;
                text-align: center;

                @media screen and (min-width: 768px) {
                    margin-bottom: 0;
                    display: block;
                    margin-right: 10px;
                    width: calc(50% - 10px);
                }

                &:hover {
                    border-bottom: 2px solid $primary;
                }
            }
        }
    }

    &-footer {
        display: flex;
        flex-flow: column;

        @media screen and (min-width: 992px) {
            flex-flow: row nowrap;
        }

        p {
            flex: 1;
            font-family: 'Apercu-Bold', sans-serif;
            font-weight: bold;
            font-size: 14px;
            line-height: 20px;
            width: 100%;
            margin-bottom: 1rem;

            &:last-of-type {
                margin-bottom: 0;
            }

            @media screen and (min-width: 992px) {
                width: 50%;
                margin-bottom: 0;
            }
        }
    }
}
</style>
